import { Component, Inject, Input, OnInit, Optional, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { SurveyService } from '../../../services/surveys.service';
import { TranslateService } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { ModalShowEvidencesComponent } from '../../../modals/modal-show-evidences/modal-show-evidences.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EChartsOption } from 'echarts';
import { ResponseTypesService } from '../../../services/config/response-types.service';

@Component({
  selector: 'app-single-question-responses',
  templateUrl: './single-question-responses.component.html',
  styleUrls: ['./single-question-responses.component.scss'],
  standalone: false,
})
export class SingleQuestionResponsesComponent implements OnInit, OnChanges {
  @Input() question;
  @Input() questionID;
  @Input() showPrivateInfo;
  @Input() viewOnlyChart = false;
  @Input() isDialog = false;
  @Input() surveyId!: number;
  @Output() aiAnalysisCompleted = new EventEmitter<void>();

  loading = false;
  heatmapOptions: EChartsOption | null = null;
  barChartOptions: EChartsOption | null = null;
  pieChartOptions: EChartsOption | null = null;

  // New ECharts Options
  multipleChoiceOptions: EChartsOption | null = null;
  checkboxOptions: EChartsOption | null = null;
  quantitativeColumnOptions: EChartsOption | null = null;
  quantitativeBoxPlotOptions: EChartsOption | null = null;
  gridRowOptions: { [key: string]: EChartsOption } = {};

  selectedTabIndex = 0;

  // ECharts options for different chart types
  multipleChoiceChartOptions: EChartsOption | null = null;
  checkboxChartOptions: EChartsOption | null = null;
  quantitativeChartOptions: EChartsOption | null = null;
  boxPlotChartOptions: EChartsOption | null = null;
  consensusChartOptions: EChartsOption | null = null;
  consensusParametersChartOptions: EChartsOption | null = null;
  gridChartOptionsMap: { [key: string]: EChartsOption } = {};
  openedSentimentChartOptions: EChartsOption | null = null; // New chart

  responses = new MatTableDataSource([]);

  constructor(
    public surveyService: SurveyService,
    public translateService: TranslateService,
    private dialog: MatDialog,
    public responsesTypeService: ResponseTypesService,
    @Optional()
    @Inject(MatDialogRef)
    public dialogRef: MatDialogRef<SingleQuestionResponsesComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  questionContentLoaded = false;
  analyticsFields = [
    { parameter: 'mean', translateString: 'generic.average' },
    {
      parameter: 'geometric_mean',
      translateString: 'generic.geometric-average',
    },
    { parameter: 'mode', translateString: 'generic.mode' },
    { parameter: 'median', translateString: 'generic.median' },
    { parameter: 'variance', translateString: 'generic.variance' },
    { parameter: 'q1', translateString: 'Q1' },
    { parameter: 'q2', translateString: 'Q2' },
    { parameter: 'q3', translateString: 'Q3' },
    { parameter: 'q4', translateString: 'Q4' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question'] && !changes['question'].firstChange) {
      this.init();
      this.generateChartOptions();
    }
  }

  ngOnInit(): void {
    if (this.data) {
      if (this.data.questionID !== undefined) {
        this.questionID = this.data.questionID;
      }
      if (this.data.showPrivateInfo !== undefined) {
        this.showPrivateInfo = this.data.showPrivateInfo;
      }
      if (this.data.isDialog !== undefined) {
        this.isDialog = this.data.isDialog;
      }
    }

    const callback = () => {
      this.init();
      this.generateChartOptions();
    };

    if (this.questionID) {
      this.loading = true;
      this.surveyService.getQuestionWithResponses(
        this.questionID,
        (question) => {
          this.question = question;
          this.loading = false;
          callback();
        },
        (err) => {
          this.loading = false;
        },
      );
    } else {
      callback();
    }
  }

  init(): void {
    const rawData = this.question.responses || [];
    const responsesArray = (rawData !== null && typeof rawData === 'object' && !Array.isArray(rawData))
      ? Object.values(rawData)
      : rawData;

    if (this.surveyService.isOpenedQuestion(this.question.typology)) {
      this.question.content = '';

      responsesArray.forEach((response: any) => {
        if (response?.answer) {
          this.question.content += response.answer + ' ';
        }
      });

      this.question.filteredContent = this.getCleanSentence(this.question.content);
      this.questionContentLoaded = true;
    }

    this.responses = new MatTableDataSource(responsesArray);
  }

  getCleanSentence(text: string): string {
    return this.surveyService.getCleanSentence(text);
  }

  getRandomColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  generateChartOptions(): void {
    if (this.surveyService.isOpenedQuestion(this.question.typology)) {
      this.generateOpenedCharts();
    }
    if (this.surveyService.isMultipleChoiceQuestion(this.question.typology)) {
      this.generateMultipleChoiceChart();
    }
    if (this.surveyService.isCheckboxQuestion(this.question.typology)) {
      this.generateCheckboxChart();
    }
    if (this.surveyService.isQuantitativeQuestion(this.question.typology)) {
      this.generateQuantitativeCharts();
    }
    if (this.surveyService.isConsensusQuestion(this.question.typology)) {
      this.generateConsensusCharts();
    }
    if (this.surveyService.isGridQuestion(this.question.typology)) {
      this.generateHeatmapOptions();
      this.generateGridCharts();
    }
  }

  private generateOpenedCharts(): void {
    let positive = 0;
    let neutral = 0;
    let negative = 0;

    const rawData = this.question.responses || [];
    const responsesArray = (rawData !== null && typeof rawData === 'object' && !Array.isArray(rawData))
      ? Object.values(rawData)
      : rawData;

    if (responsesArray.length === 0) return;

    responsesArray.forEach((response: any) => {
      if (response.status === 'ABSTAINED') return;
      const score = typeof response.score === 'string' ? parseFloat(response.score) : (response.score || 0);
      if (score > 0) positive++;
      else if (score < 0) negative++;
      else neutral++;
    });

    this.openedSentimentChartOptions = {
        title: {
            text: this.translateService.instant('generic.sentiment-analysis') || 'Analisi del Sentiment',
            left: 'center',
            top: 20,
            textStyle: {
                fontSize: 16,
                fontWeight: 'bold',
                color: '#333'
            }
        },
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0 },
        series: [
            {
                name: 'Sentiment',
                type: 'pie',
                radius: ['45%', '70%'],
                center: ['50%', '55%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 10,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                label: { show: false },
                labelLine: { show: false },
                data: [
                    { value: positive, name: this.translateService.instant('generic.positive') || 'Positivo', itemStyle: { color: '#00bc1d' } },
                    { value: neutral, name: this.translateService.instant('generic.neutral') || 'Neutro', itemStyle: { color: '#cccccc' } },
                    { value: negative, name: this.translateService.instant('generic.negative') || 'Negativo', itemStyle: { color: '#ff0000' } }
                ]
            }
        ]
    };
  }

  private generateMultipleChoiceChart(): void {
    const data: any[] = [];

    this.question?.choices?.forEach(choice => {
      data.push({
        name: choice.title,
        value: choice.answers_count
      });
    });

    const abstainedAnswers = this.responses?.data?.filter((response) => response.status === 'ABSTAINED');
    if (abstainedAnswers.length > 0) {
      data.push({
        name: this.translateService.instant('generic.users-abstained'),
        value: abstainedAnswers.length
      });
    }

    this.multipleChoiceChartOptions = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: <br/> {c} ({d}%)',
        extraCssText: 'max-width: 300px; white-space: pre-wrap; word-break: break-word;'
      },
      legend: {
        type: 'scroll',
        orient: 'vertical',
        right: 10,
        top: 20,
        bottom: 20,
        textStyle: {
          color: '#666',
          width: 300,
          overflow: 'break'
        }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['25%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            width: 150,
            overflow: 'truncate', // Truncate slice label to keep chart clean, tooltip shows full
            formatter: '{b}'
          },
          labelLine: { show: true },
          data: data
        }
      ]
    };
  }

  private generateCheckboxChart(): void {
    const categories: string[] = [];
    const values: number[] = [];
    const colors: string[] = [];

    this.question?.choices?.forEach(choice => {
      categories.push(choice.title);
      values.push(choice.answers_count);
      colors.push(choice.color || this.getRandomColor());
    });

    const abstainedAnswers = this.responses?.data?.filter((response) => response.status === 'ABSTAINED');
    if (abstainedAnswers.length > 0) {
      categories.push(this.translateService.instant('generic.users-abstained'));
      values.push(abstainedAnswers.length);
      colors.push(this.getRandomColor());
    }

    this.checkboxChartOptions = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        show: true,
        top: 'top',
        textStyle: { color: '#666' }
      },
      grid: {
        left: '25%', // Increased left margin extensively for full titles
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        minInterval: 1
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          interval: 0,
          width: 300,        // Allows breaking text
          overflow: 'break'
        }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'bar',
          data: values.map((value, index) => ({
            value: value,
            itemStyle: { color: colors[index] }
          })),
          label: {
            show: true,
            position: 'right'
          }
        }
      ]
    };
  }

  private generateQuantitativeCharts(): void {
    const max_range = parseFloat(this.question?.max);
    const min_range = parseFloat(this.question?.min);
    const step = parseFloat(this.question?.step) || 1;
    const answerCount = this.question?.incidence;

    for (let i = min_range; i <= max_range; i += step) {
      const key = i.toFixed(2);
      if (!(key in answerCount)) {
        answerCount[key] = 0;
      }
    }

    const categories: string[] = [];
    const values: number[] = [];

    Object.keys(answerCount).sort((a, b) => parseFloat(a) - parseFloat(b)).forEach(key => {
      categories.push(key);
      values.push(answerCount[key]);
    });

    this.quantitativeChartOptions = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        show: true,
        top: 'top',
        textStyle: { color: '#666' }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories
      },
      yAxis: {
        type: 'value',
        minInterval: 1
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'bar',
          data: values,
          itemStyle: {
            color: '#5470c6'
          }
        }
      ]
    };

    // Box plot using candlestick chart
    const results = this.question?.quantitative_results;
    this.boxPlotChartOptions = {
      title: {
        text: 'Box Plot',
        left: 'center'
      },
      tooltip: {
        trigger: 'item'
      },
      legend: {
        show: true,
        top: 'bottom',
        textStyle: { color: '#666' }
      },
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '15%'
      },
      xAxis: {
        type: 'category',
        data: ['Data']
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Box Plot',
          type: 'candlestick',
          data: [[results?.min, results?.q1, results?.q3, results?.max]],
          itemStyle: {
            color: '#888',
            color0: '#888',
            borderColor: '#888',
            borderColor0: '#888'
          }
        },
        {
          name: 'Median',
          type: 'line',
          data: [results?.median],
          symbol: 'rect',
          symbolSize: 10,
          itemStyle: {
            color: '#1b61a1'
          }
        }
      ]
    };
  }

  private generateConsensusCharts(): void {
    const consensusData = [
      {
        name: this.translateService.instant('generic.agree'),
        value: this.question.agreeCount,
        itemStyle: { color: '#00bc1d' }
      },
      {
        name: this.translateService.instant('generic.disagree'),
        value: this.question.disagreeCount,
        itemStyle: { color: '#ff0000' }
      },
      {
        name: this.translateService.instant('generic.abstained'),
        value: this.question.abstainedCount,
        itemStyle: { color: '#e5e3e3' }
      }
    ];

    this.consensusChartOptions = {
      tooltip: { trigger: 'item' },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#666' }
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'pie',
          radius: ['40%', '70%'],
          data: consensusData,
          label: { show: false },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };

    const certaintyDegree = Math.round((Number(this.question?.results?.certainty_degree) || 0) * 100) / 100;
    const contradictionDegree = Math.round((Number(this.question?.results?.contradiction_degree) || 0) * 100) / 100;

    this.consensusParametersChartOptions = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '5%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        max: 100
      },
      yAxis: {
        type: 'category',
        data: [
          this.translateService.instant('generic.certainty-degree'),
          this.translateService.instant('generic.contradiction-degree')
        ]
      },
      series: [
        {
          type: 'bar',
          data: [
            { value: certaintyDegree, itemStyle: { color: '#1b61a1' } },
            { value: contradictionDegree, itemStyle: { color: '#ff0000' } }
          ],
          label: {
            show: true,
            position: 'right',
            formatter: '{c}%'
          }
        }
      ]
    };
  }

  private generateGridCharts(): void {
    const rows = this.question.grid_rows;
    const cols = this.question.grid_columns;
    this.gridRowOptions = {};

    rows.forEach(row => {
      const data: any[] = [];

      Object.keys(row?.answers_count)?.forEach((colId) => {
        const col = cols?.find((c) => c.id == colId);
        if (col) {
          data.push({
            name: col.title,
            value: row?.answers_count[colId] || 0
          });
        }
      });

      this.gridChartOptionsMap[row.id] = {
        tooltip: { trigger: 'item' },
        legend: {
          orient: 'vertical',
          left: 'left',
          textStyle: { color: '#666', fontSize: 10 }
        },
        series: [
          {
            name: 'Responses',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 5,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: { show: true, fontSize: 10 },
            data: data
          }
        ]
      };
    });
  }

  private generateHeatmapOptions(): void {
    const rows = this.question.grid_rows.reverse();
    const cols = this.question.grid_columns;

    const heatmapData: (number | string)[][] = [];
    let maxValue = 0;
    cols?.forEach((col, colIndex) => {
      rows.forEach((row, rowIndex) => {
        const value = row?.answers_count[col?.id] || 0;
        if (value > maxValue) {
          maxValue = value;
        }
        heatmapData.push([colIndex, rowIndex, value]);
      });
    });

    // Calcolo dinamico dei margini
    const maxRowLabelLength = Math.max(...rows.map((r) => r.title.length));
    const maxColLabelLength = Math.max(...cols.map((c) => c.title.length));

    const leftMargin = Math.min(200, 12 + maxRowLabelLength * 7); // max 300px
    const bottomMargin = Math.min(200, 20 + maxColLabelLength * 6); // max 200px

    this.heatmapOptions = {
      tooltip: {
        position: 'top',
      },
      grid: {
        top: '10%',
        left: leftMargin,
        bottom: bottomMargin,
        right: 20,
      },
      xAxis: {
        type: 'category',
        data: cols.map((c) => c.title.replace(/(.{15})/g, '$1\n')), // wrapping soft
        axisLabel: {
          fontSize: 12,
          lineHeight: 16,
          interval: 0,
        },
        splitArea: {
          show: true,
        },
      },
      yAxis: {
        type: 'category',
        data: rows.map((r) => r.title.replace(/(.{25})/g, '$1\n')),
        axisLabel: {
          fontSize: 12,
          lineHeight: 16,
        },
        splitArea: {
          show: true,
        },
      },
      visualMap: {
        min: 0,
        max: maxValue,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '5%',
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: true,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
    this.selectedTabIndex = 0;
  }

  showExpertEvideces(element): void {
    const dialogRef = this.dialog.open(ModalShowEvidencesComponent, {
      panelClass: 'medium-dialog',
      data: {
        attachments: element?.attachments,
      },
    });

    dialogRef.afterClosed().subscribe((event) => {});
  }

  /** Domande con risposte testuali lunghe: usa righe espandibili (stessa grafica di OPENED) */
  get hasExpandableRows(): boolean {
    const typology = this.question?.typology;
    return (
      this.surveyService.isOpenedQuestion(typology) ||
      this.surveyService.isMultipleChoiceQuestion(typology) ||
      this.surveyService.isQuantitativeQuestion(typology) ||
      this.surveyService.isConsensusQuestion(typology) ||
      this.surveyService.isGridQuestion(typology)
    );
  }

  collapsedRows = new Set<any>();

  toggleExpandedRow(element: any): void {
    if (this.collapsedRows.has(element)) {
      this.collapsedRows.delete(element);
    } else {
      this.collapsedRows.add(element);
    }
    this.collapsedRows = new Set(this.collapsedRows);
  }

  isRowExpanded(element: any): boolean {
    return !this.collapsedRows.has(element);
  }

  hasAnswer(element: any): boolean {
    if (!element) return false;
    if (this.surveyService.isMultipleChoiceQuestion(this.question?.typology)) {
      return !!(element?.answer || element?.open_response_value);
    }
    if (this.surveyService.isQuantitativeQuestion(this.question?.typology)) {
      const a = element?.answer;
      return a != null && a !== 'N/A' && (typeof a === 'number' || String(a).trim() !== '');
    }
    const answer = element?.answer;
    return !!answer && answer !== 'N/A' && String(answer).trim() !== '';
  }

  hasFeedback(element: any): boolean {
    const feedback = element?.feedback;
    return !!feedback && feedback !== 'N/A' && String(feedback).trim() !== '';
  }

  /** Riga espandibile: quando c'è feedback oppure (MULTIPLE_CHOICE con risposta) */
  canExpandRow(element: any): boolean {
    return (
      this.surveyService.isOpenedQuestion(this.question?.typology) ||
      this.hasFeedback(element) ||
      (this.surveyService.isMultipleChoiceQuestion(this.question?.typology) && this.hasAnswer(element))
    );
  }

  getUserDisplayIndex(element: any): number {
    const data = this.responses?.data ?? [];
    const idx = data.indexOf(element);
    return idx >= 0 ? idx + 1 : 1;
  }

  get columnsForTypology(): string[] {
    const typology = this.question?.typology;
    const baseColumns = this.surveyService.isConsensusQuestion(typology)
      ? ['name', 'sentiment', 'answer', 'feedback', 'confidence', 'evidences']
      : this.surveyService.isGridQuestion(typology)
        ? ['name', 'feedback', 'evidences']
        : this.surveyService.isOpenedQuestion(typology)
          ? ['name', 'sentiment', 'answer', 'feedback', 'evidences']
          : (
              this.surveyService.isMultipleChoiceQuestion(typology) ||
              this.surveyService.isCheckboxQuestion(typology) ||
              this.surveyService.isQuantitativeQuestion(typology)
            )
            ? ['name', 'answer', 'feedback', 'evidences']
            : ['name', 'answer', 'feedback', 'evidences'];

    return this.hasExpandableRows ? [...baseColumns, 'expand'] : baseColumns;
  }

  closeDialogue(event: any = null) {
    this.dialogRef.close(event);
  }

  onAiAnalysisCompleted() {
    console.log('📤 AI Analysis completed, propagating event to parent');
    this.aiAnalysisCompleted.emit();
  }
}
